import { baseApiClient as api } from "../baseService";

export const addTagTypes = ["blob-sink-config"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      firmsBlobSinkConfigsList: build.query<
        FirmsBlobSinkConfigsListApiResponse,
        FirmsBlobSinkConfigsListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/blob-sink-configs/`,
        }),
        providesTags: ["blob-sink-config"],
      }),
      firmsBlobSinkConfigsCreate: build.mutation<
        FirmsBlobSinkConfigsCreateApiResponse,
        FirmsBlobSinkConfigsCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/blob-sink-configs/`,
          method: "POST",
          body: queryArg.createBlobSinkConfigSchema,
        }),
        invalidatesTags: ["blob-sink-config"],
      }),
      firmsBlobSinkConfigsRetrieve: build.query<
        FirmsBlobSinkConfigsRetrieveApiResponse,
        FirmsBlobSinkConfigsRetrieveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/blob-sink-configs/${queryArg.id}/`,
        }),
        providesTags: ["blob-sink-config"],
      }),
      firmsBlobSinkConfigsPartialUpdate: build.mutation<
        FirmsBlobSinkConfigsPartialUpdateApiResponse,
        FirmsBlobSinkConfigsPartialUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/blob-sink-configs/${queryArg.id}/`,
          method: "PATCH",
          body: queryArg.patchedUpdateBlobSinkConfigSchema,
        }),
        invalidatesTags: ["blob-sink-config"],
      }),
      firmsBlobSinkConfigsDestroy: build.mutation<
        FirmsBlobSinkConfigsDestroyApiResponse,
        FirmsBlobSinkConfigsDestroyApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/blob-sink-configs/${queryArg.id}/`,
          method: "DELETE",
        }),
        invalidatesTags: ["blob-sink-config"],
      }),
      firmsBlobSinkConfigsGuessConvention: build.query<
        FirmsBlobSinkConfigsGuessConventionApiResponse,
        FirmsBlobSinkConfigsGuessConventionApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/blob-sink-configs/guess_convention/`,
          params: {
            target_system: queryArg.targetSystem,
          },
        }),
        providesTags: ["blob-sink-config"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type FirmsBlobSinkConfigsListApiResponse =
  /** status 200  */ BlobSinkConfigSchema[];
export type FirmsBlobSinkConfigsListApiArg = {
  firmId: string;
};
export type FirmsBlobSinkConfigsCreateApiResponse =
  /** status 201  */ BlobSinkConfigSchema;
export type FirmsBlobSinkConfigsCreateApiArg = {
  firmId: string;
  createBlobSinkConfigSchema: CreateBlobSinkConfigSchema;
};
export type FirmsBlobSinkConfigsRetrieveApiResponse =
  /** status 200  */ BlobSinkConfigSchema;
export type FirmsBlobSinkConfigsRetrieveApiArg = {
  firmId: string;
  id: string;
  /** The UUID of the blob sink configuration */
  pk: string;
};
export type FirmsBlobSinkConfigsPartialUpdateApiResponse =
  /** status 200  */ BlobSinkConfigSchema;
export type FirmsBlobSinkConfigsPartialUpdateApiArg = {
  firmId: string;
  id: string;
  /** The UUID of the blob sink configuration */
  pk: string;
  patchedUpdateBlobSinkConfigSchema: UpdateBlobSinkConfigSchema;
};
export type FirmsBlobSinkConfigsDestroyApiResponse = unknown;
export type FirmsBlobSinkConfigsDestroyApiArg = {
  firmId: string;
  id: string;
  /** The UUID of the blob sink configuration */
  pk: string;
};
export type FirmsBlobSinkConfigsGuessConventionApiResponse =
  /** status 200  */ GuessConventionResponseSchema;
export type FirmsBlobSinkConfigsGuessConventionApiArg = {
  firmId: string;
  /** The target system to guess the convention for (e.g., FILEVINE) */
  targetSystem:
    | "CLIO"
    | "FILEVINE"
    | "NEOS"
    | "SMARTADVOCATE"
    | "LITIFY"
    | "CASEPEER"
    | "MYCASE";
};
export type BlobSinkActionTargetSystem =
  | "FILEVINE"
  | "LITIFY"
  | "NEOS"
  | "CLIO"
  | "SMARTADVOCATE"
  | "CASEPEER"
  | "MYCASE";
export type BlobSinkApprovalMode = "AUTOMATIC" | "HUMAN";
export type BlobSinkConfigSchema = {
  id: string | string;
  firm_id: string | string;
  target_system: BlobSinkActionTargetSystem;
  is_enabled: boolean;
  default_approval_mode: BlobSinkApprovalMode;
  file_location_prompt: string;
  push_window_start_time?: string | null;
  push_window_end_time?: string | null;
  push_window_timezone?: string | null;
};
export type CreateBlobSinkConfigSchema = {
  firm_id: string | string;
  target_system: BlobSinkActionTargetSystem;
  is_enabled?: boolean;
  default_approval_mode?: BlobSinkApprovalMode;
  file_location_prompt?: string;
  push_window_start_time?: string | null;
  push_window_end_time?: string | null;
  push_window_timezone?: string | null;
};
export type UpdateBlobSinkConfigSchema = {
  is_enabled?: boolean | null;
  default_approval_mode?: BlobSinkApprovalMode;
  file_location_prompt?: string | null;
  push_window_start_time?: string | null;
  push_window_end_time?: string | null;
  push_window_timezone?: string | null;
};
export type GuessConventionResponseSchema = {
  convention: string | null;
  target_system: BlobSinkActionTargetSystem;
};
export const {
  useFirmsBlobSinkConfigsListQuery,
  useFirmsBlobSinkConfigsCreateMutation,
  useFirmsBlobSinkConfigsRetrieveQuery,
  useFirmsBlobSinkConfigsPartialUpdateMutation,
  useFirmsBlobSinkConfigsDestroyMutation,
  useFirmsBlobSinkConfigsGuessConventionQuery,
} = injectedRtkApi;
